import React from 'react'
import {
	SyncOutlined,
	ThunderboltOutlined,
	EditOutlined,
	CloseCircleOutlined,
	EyeOutlined,
	CheckCircleFilled,
	SunFilled
} from '@ant-design/icons'
import PropTypes from 'prop-types'
import CustomTag from './CustomTag'
import { useSmartTranslation } from '../../cls/TranslationContext'
/**
 * StatusTag component renders a styled tag (via CustomTag) with an icon or text
 * based on the `tag` value and chosen `tagType`. Supports multiple categories:
 * - StatusTag: sprint/request status (draft, active, completed, etc.)
 * - PriorityTag: priority levels (low, medium, high, urgent)
 * - RequestStatusTag: request read/unread
 * - NotificationStatusTag: notification read/unread
 * - NotificationTypeTag: notification type (new, update, progress)
 *
 * @component
 * @param {Object} props
 * @param {string|number} props.tag
 *   The key identifying which tag to render (e.g. 'draft', 'completed', 1, 'new', etc.).
 * @param {React.ReactNode} [props.fallback]
 *   Optional fallback content to render if `tag` is not found in the selected mapping.
 * @param {boolean} [props.isShort=false]
 *   When true, renders only the icon form of the tag instead of full text.
 * @param {'StatusTag'|'PriorityTag'|'RequestStatusTag'|'NotificationStatusTag'|'NotificationTypeTag'} [props.tagType='StatusTag']
 *   Selects which internal mapping of tags to use.
 * @param {'sprint'|'request'} [props.type='sprint']
 *   Context for certain tooltips/text variations.
 * @param {Object} [props.style]
 *   Additional style props passed down to CustomTag.
 * @returns {JSX.Element}
 */
const StatusTag = ({
	tag,
	fallback,
	isShort,
	tagType = 'StatusTag',
	type = 'sprint',
	...props
}) => {
	const { t } = useSmartTranslation()
	let tags = {}
	if (tagType == 'StatusTag') {
		tags = {
			canceled: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__progress-x%single.canceled')}
					color="error"
					{...props}
				>
					{!isShort ? t('single.canceled') : <CloseCircleOutlined />}
				</CustomTag>
			),
			draft: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__progress-x%single.draft')}
					color="warning"
					{...props}
				>
					{!isShort ? t('single.draft') : <EditOutlined />}
				</CustomTag>
			),
			in_review: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__progress-x%single.in-review')}
					color="warning"
					{...props}
				>
					{!isShort ? t('single.in-review') : <EyeOutlined />}
				</CustomTag>
			),
			active: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__progress-x%single.active')}
					color="default"
					{...props}
				>
					{!isShort && t('single.active')}
					<ThunderboltOutlined />
				</CustomTag>
			),
			pending: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__progress-x%single.pending')}
					icon={!isShort && <SyncOutlined spin />}
					color="processing"
					{...props}
				>
					{!isShort ? (
						t('single.processing')
					) : (
						<SyncOutlined spin />
					)}
				</CustomTag>
			),
			completed: (
				<CustomTag
					className="we-status-tag"
					tooltip={
						type === 'request'
							? t('__progress-x%single.read')
							: t('__progress-x%single.completed')
					}
					color="success"
					{...props}
				>
					{!isShort ? (
						type === 'request' ? (
							t('single.read')
						) : (
							t('single.completed')
						)
					) : (
						<CheckCircleFilled />
					)}
				</CustomTag>
			),
			default: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__progress-x%single.default')}
					color="default"
					{...props}
				>
					-
				</CustomTag>
			)
		}
	} else if (tagType == 'PriorityTag') {
		tags = {
			1: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__priority-x%single.medium')}
					color="#FFD700"
					{...props}
				>
					<SunFilled />
				</CustomTag>
			),
			2: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__priority-x%single.high')}
					color="#FFA500"
					{...props}
				>
					<SunFilled />
				</CustomTag>
			),
			3: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__priority-x%single.urgent')}
					color="#FF0000"
					{...props}
				>
					<SunFilled />
				</CustomTag>
			),
			default: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__priority-x%single.low')}
					color="#808080"
					{...props}
				>
					<SunFilled />
				</CustomTag>
			)
		}
	} else if (tagType == 'RequestStatusTag') {
		tags = {
			completed: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.completed')}
					color="success"
					{...props}
				>
					{!isShort ? (
						t('single.completed')
					) : (
						<CheckCircleFilled />
					)}
				</CustomTag>
			),
			not_read: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.not-read')}
					color="warning"
					{...props}
				>
					{!isShort ? t('single.not-read') : <EyeOutlined />}
				</CustomTag>
			),
			default: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.unknown')}
					color="default"
					{...props}
				>
					-
				</CustomTag>
			)
		}
	} else if (tagType == 'NotificationStatusTag') {
		tags = {
			read: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.read')}
					color="success"
					{...props}
				>
					{!isShort ? t('single.read') : <CheckCircleFilled />}
				</CustomTag>
			),
			not_read: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.not-read')}
					color="warning"
					{...props}
				>
					{!isShort ? t('single.not-read') : <EyeOutlined />}
				</CustomTag>
			),
			default: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.unknown')}
					color="default"
					{...props}
				>
					-
				</CustomTag>
			)
		}
	} else if (tagType == 'NotificationTypeTag') {
		tags = {
			new: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__type-x%single.new')}
					color="green"
					{...props}
				>
					{!isShort ? t('single.new') : <CheckCircleFilled />}
				</CustomTag>
			),
			update: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__type-x%single.update')}
					color="orange"
					{...props}
				>
					{!isShort ? t('single.update') : <EditOutlined />}
				</CustomTag>
			),
			progress: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__type-x%__progress-x')}
					color="blue"
					{...props}
				>
					{!isShort ? t('single.progress') : <SyncOutlined spin />}
				</CustomTag>
			),
			default: (
				<CustomTag
					className="we-status-tag"
					tooltip={t('__status-x%single.unknown')}
					color="gray"
					{...props}
				>
					-
				</CustomTag>
			)
		}
	}

	if (!tag || !tags[tag]) return fallback || tags.default

	return tags[tag]
}

StatusTag.propTypes = {
	tag: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	tagType: PropTypes.oneOf([
		'StatusTag',
		'PriorityTag',
		'RequestStatusTag',
		'NotificationStatusTag',
		'NotificationTypeTag'
	]),
	fallback: PropTypes.node,
	style: PropTypes.object,
	isShort: PropTypes.bool
}

export default StatusTag
